import React, { useEffect, useState } from 'react'
import { Box, Button, TextField, Typography } from '@mui/material'
import { useNotify } from 'react-admin'

import { IDwMargin, getDwMargin, setDwMarginRate } from '../../../Services/contractDates'

interface Props {
	billingAccountId: string
	onSaved: () => void
}

// Le taux de marge appliqué à la refacturation du datawarehouse.
//
// ⚠️ Saisi en POURCENTAGE, stocké en FRACTION. Un opérateur qui tape « 25 »
// pense 25 %, et un champ qui attendrait 0,25 finirait par recevoir 25 — soit
// 2 500 % de marge, sur une facture.

// #region DwMarginField
export const DwMarginField: React.FC<Props> = ({ billingAccountId, onSaved }) => {
	const notify = useNotify()
	const [margin, setMargin] = useState<IDwMargin | null>(null)
	const [value, setValue] = useState('')
	const [saving, setSaving] = useState(false)

	useEffect(() => {
		if (!billingAccountId) return
		getDwMargin(billingAccountId)
			.then(setMargin)
			.catch(() => setMargin(null))
	}, [billingAccountId])

	const asPercent = margin?.rate === null || margin?.rate === undefined ? '' : String(margin.rate * 100)
	useEffect(() => setValue(asPercent), [asPercent])

	const dirty = value !== asPercent
	const n = Number(value)
	const invalid = value !== '' && (!Number.isFinite(n) || n < 0)

	const save = async () => {
		setSaving(true)
		try {
			// Champ vidé = revenir au défaut de la plateforme, et non « 0 % ».
			await setDwMarginRate(billingAccountId, value === '' ? null : n / 100)
			setMargin(await getDwMargin(billingAccountId))
			notify('Taux de marge enregistré', { type: 'success' })
			onSaved()
		} catch (e: any) {
			notify(e?.message || 'Échec de l’enregistrement', { type: 'error' })
		} finally {
			setSaving(false)
		}
	}

	return (
		<Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(0,0,0,0.08)' }}>
			<Typography variant="subtitle2" gutterBottom>
				Marge datawarehouse
			</Typography>
			<Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
				<TextField
					label="Taux (%)"
					size="small"
					value={value}
					onChange={(e) => setValue(e.target.value)}
					error={invalid}
					placeholder={margin ? String(margin.platformRate * 100) : 'défaut'}
					sx={{ width: 140 }}
				/>
				<Button variant="outlined" size="small" disabled={!dirty || invalid || saving} onClick={save}>
					{saving ? '…' : 'Enregistrer'}
				</Button>
			</Box>
			{/* ⚠️ La phrase qui compte. Sans elle, on modifiera ce taux en croyant
			    corriger une facture en cours — alors qu'il ne touchera que les
			    journées mesurées à partir de maintenant. */}
			{/* ⚠️ Dire d'OÙ vient le taux, et pas seulement sa valeur. Sans ça, on
			    saisit le défaut « pour être sûr » — ce qui DÉTACHE le compte du
			    défaut, et le jour où la plateforme change de taux, ce compte-là ne
			    suit plus sans que personne ne s'en aperçoive. */}
			<Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
				{margin && margin.rate === null
					? `Suit le taux de la plateforme (${(margin.platformRate * 100).toFixed(0)} %). Laisse vide pour continuer à le suivre.`
					: margin
						? `Taux négocié pour ce compte. Vide-le pour revenir au taux de la plateforme (${(margin.platformRate * 100).toFixed(0)} %).`
						: ''}
			</Typography>
			<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
				Ne s’applique qu’aux journées mesurées ensuite : chaque journée garde le taux qu’elle
				portait, pour qu’une révision de Google ne change pas un prix déjà constaté.
			</Typography>
		</Box>
	)
}

// #endregion
